/**
 * Recurring Booking utilities.
 *
 * Generates a series of booking dates for weekly or monthly recurring bookings.
 *
 * Config stored in transaction protectedData.recurrence = {
 *   frequency: 'weekly' | 'biweekly' | 'monthly',
 *   occurrences: number (1-52),
 *   parentTxId: string | null
 * }
 */

export const FREQUENCIES = ['weekly', 'biweekly', 'monthly'];
export const MAX_OCCURRENCES = 52;
export const MIN_OCCURRENCES = 2;

/**
 * Generate recurring booking start dates.
 * @param {Date|string} firstStart — first booking start date
 * @param {string} frequency — 'weekly' | 'biweekly' | 'monthly'
 * @param {number} occurrences — number of bookings to generate
 * @returns {Date[]} — array of start dates (including first)
 */
export const generateRecurringDates = (firstStart, frequency, occurrences) => {
  const count = Math.min(Math.max(occurrences, MIN_OCCURRENCES), MAX_OCCURRENCES);
  const dates = [];
  let current = new Date(firstStart);

  for (let i = 0; i < count; i++) {
    dates.push(new Date(current));

    if (frequency === 'weekly') {
      current = new Date(current.getTime() + 7 * 24 * 3600 * 1000);
    } else if (frequency === 'biweekly') {
      current = new Date(current.getTime() + 14 * 24 * 3600 * 1000);
    } else if (frequency === 'monthly') {
      current = new Date(current);
      current.setMonth(current.getMonth() + 1);
    }
  }

  return dates;
};

/**
 * Get recurrence config from transaction.
 * @param {Object|null} transaction
 * @returns {Object|null}
 */
export const getRecurrence = (transaction) =>
  transaction?.attributes?.protectedData?.recurrence || null;

/**
 * Validate recurrence config.
 * @param {{ frequency, occurrences }} config
 * @returns {string|null}
 */
export const validateRecurrence = ({ frequency, occurrences }) => {
  if (!FREQUENCIES.includes(frequency)) return `Invalid frequency. Must be: ${FREQUENCIES.join(', ')}`;
  const n = parseInt(occurrences, 10);
  if (isNaN(n) || n < MIN_OCCURRENCES) return `Minimum ${MIN_OCCURRENCES} occurrences`;
  if (n > MAX_OCCURRENCES) return `Maximum ${MAX_OCCURRENCES} occurrences`;
  return null;
};
